import { parsePinyin, SANDHI_EXEMPT, toToneMarked, type Syllable } from './pinyin'

export type TokenStatus =
  /** Right unit, right tone. */
  | 'correct'
  /** Right syllable, wrong tone. Only ever produced by the pinyin grader. */
  | 'tone'
  /** Wrong unit in the right position. */
  | 'wrong'
  /** In the target, absent from the attempt. */
  | 'missing'
  /** In the attempt, not in the target. */
  | 'extra'

export interface GradedToken {
  status: TokenStatus
  /** What the target expected. Empty for `extra`. */
  expected: string
  /** What the learner produced. Empty for `missing`. */
  got: string
  /** 0 for neutral/unknown; drives the tone ribbon. */
  expectedTone: number
  gotTone: number
}

export interface GradeResult {
  /** 0-100. The headline number. */
  score: number
  /** 0-100 across syllables whose base was right. Null when nothing matched. */
  toneAccuracy: number | null
  tokens: GradedToken[]
  counts: Record<TokenStatus, number>
  /** Number of units the target contained. */
  targetLength: number
}

type OpKind = 'match' | 'sub' | 'del' | 'ins'
interface Op {
  kind: OpKind
  a: number
  b: number
}

/**
 * Levenshtein alignment with backtrace. `a` is the target, `b` the attempt;
 * `del` means the learner dropped a unit, `ins` means they added one.
 *
 * Equality is supplied by the caller so the pinyin grader can align on the
 * syllable base and judge tone separately — aligning on base+tone would report
 * every tone slip as an unrelated substitution.
 */
function align<T>(a: T[], b: T[], eq: (x: T, y: T) => boolean): Op[] {
  const n = a.length
  const m = b.length
  const cost: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0))

  for (let i = 0; i <= n; i++) cost[i][0] = i
  for (let j = 0; j <= m; j++) cost[0][j] = j

  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      const sub = cost[i - 1][j - 1] + (eq(a[i - 1], b[j - 1]) ? 0 : 1)
      cost[i][j] = Math.min(sub, cost[i - 1][j] + 1, cost[i][j - 1] + 1)
    }
  }

  const ops: Op[] = []
  let i = n
  let j = m
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0) {
      const same = eq(a[i - 1], b[j - 1])
      if (cost[i][j] === cost[i - 1][j - 1] + (same ? 0 : 1)) {
        ops.push({ kind: same ? 'match' : 'sub', a: i - 1, b: j - 1 })
        i--
        j--
        continue
      }
    }
    if (i > 0 && cost[i][j] === cost[i - 1][j] + 1) {
      ops.push({ kind: 'del', a: i - 1, b: -1 })
      i--
      continue
    }
    ops.push({ kind: 'ins', a: -1, b: j - 1 })
    j--
  }
  return ops.reverse()
}

function emptyCounts(): Record<TokenStatus, number> {
  return { correct: 0, tone: 0, wrong: 0, missing: 0, extra: 0 }
}

function summarise(tokens: GradedToken[], targetLength: number, credit: number): GradeResult {
  const counts = emptyCounts()
  for (const t of tokens) counts[t.status]++

  // Extra units are penalised, but never below zero — a learner who writes the
  // sentence correctly and then adds a stray clause should not score worse than
  // one who wrote nothing.
  const raw = credit - counts.extra * 0.5
  const score = targetLength === 0 ? 0 : Math.max(0, Math.min(100, (raw / targetLength) * 100))

  const baseRight = counts.correct + counts.tone
  return {
    score: Math.round(score),
    toneAccuracy: baseRight === 0 ? null : Math.round((counts.correct / baseRight) * 100),
    tokens,
    counts,
    targetLength,
  }
}

/** Characters we ignore entirely when comparing — IMEs vary on punctuation. */
const IGNORED = /[\s，。！？；：、“”‘’（）《》〈〉【】…—·,.!?;:'"()\[\]{}<>]/gu

function hanziChars(s: string): string[] {
  return Array.from(s.replace(IGNORED, ''))
}

/**
 * Character-exact grading for hanzi typed with a Chinese IME. Punctuation and
 * spacing are ignored; every remaining character must match.
 */
export function gradeHanzi(target: string, attempt: string): GradeResult {
  const a = hanziChars(target)
  const b = hanziChars(attempt)
  const ops = align(a, b, (x, y) => x === y)

  const tokens: GradedToken[] = ops.map((op) => {
    switch (op.kind) {
      case 'match':
        return { status: 'correct', expected: a[op.a], got: b[op.b], expectedTone: 0, gotTone: 0 }
      case 'sub':
        return { status: 'wrong', expected: a[op.a], got: b[op.b], expectedTone: 0, gotTone: 0 }
      case 'del':
        return { status: 'missing', expected: a[op.a], got: '', expectedTone: 0, gotTone: 0 }
      default:
        return { status: 'extra', expected: '', got: b[op.b], expectedTone: 0, gotTone: 0 }
    }
  })

  const credit = tokens.filter((t) => t.status === 'correct').length
  return summarise(tokens, a.length, credit)
}

/**
 * Tone credit rules. A learner is not wrong about the word when they give 一 or
 * 不 its dictionary tone rather than its sandhi tone, and neutral-tone syllables
 * are transcribed inconsistently across textbooks — so both are tone-exempt.
 */
function toneIsAcceptable(expected: Syllable, got: Syllable): boolean {
  if (expected.tone === got.tone) return true
  if (expected.tone === 0) return true
  return SANDHI_EXEMPT.has(expected.base)
}

/**
 * Syllable-and-tone grading for pinyin typed on a plain keyboard.
 *
 * When `requireTones` is false a tone slip still shows up in the ribbon as
 * `tone` but earns full credit, so the same drill can be run strictly or gently
 * without changing the display.
 */
export function gradePinyin(target: string, attempt: string, requireTones: boolean): GradeResult {
  const a = parsePinyin(target)
  const b = parsePinyin(attempt)
  const ops = align(a, b, (x, y) => x.base !== '' && x.base === y.base)

  const tokens: GradedToken[] = ops.map((op) => {
    switch (op.kind) {
      case 'match': {
        const exp = a[op.a]
        const got = b[op.b]
        return {
          status: toneIsAcceptable(exp, got) ? 'correct' : 'tone',
          expected: toToneMarked(exp),
          got: toToneMarked(got),
          expectedTone: exp.tone,
          gotTone: got.tone,
        }
      }
      case 'sub':
        return {
          status: 'wrong',
          expected: toToneMarked(a[op.a]),
          got: b[op.b].base ? toToneMarked(b[op.b]) : b[op.b].raw,
          expectedTone: a[op.a].tone,
          gotTone: b[op.b].tone,
        }
      case 'del':
        return {
          status: 'missing',
          expected: toToneMarked(a[op.a]),
          got: '',
          expectedTone: a[op.a].tone,
          gotTone: 0,
        }
      default:
        return {
          status: 'extra',
          expected: '',
          got: b[op.b].base ? toToneMarked(b[op.b]) : b[op.b].raw,
          expectedTone: 0,
          gotTone: b[op.b].tone,
        }
    }
  })

  const credit = tokens.reduce((sum, t) => {
    if (t.status === 'correct') return sum + 1
    if (t.status === 'tone') return sum + (requireTones ? 0.5 : 1)
    return sum
  }, 0)

  return summarise(tokens, a.length, credit)
}

export function grade(
  target: { hanzi: string; pinyin: string },
  attempt: string,
  mode: 'hanzi' | 'pinyin',
  requireTones: boolean,
): GradeResult {
  return mode === 'hanzi'
    ? gradeHanzi(target.hanzi, attempt)
    : gradePinyin(target.pinyin, attempt, requireTones)
}
